class EmbroideryConfigurator extends HTMLElement {
  connectedCallback() {
    // Initialize when the drawer opens
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isVisible = document.body.classList.contains('embroidery-drawer-is-open');
          if (isVisible && !this.isInitialized) {
            this.initialize();
          }
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  /**
   * Initializes the embroidery configurator
   * Sets up configuration, elements, state, and event bindings
   * Prevents multiple initializations
   */
  initialize() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    this.initializeConfig();
    this.initializeElements();
    this.initializeState();
    this.bindEvents();
    this.initializeLogoVariantTitles();

    // Set initial step
    this.state.embroidery.step = 'step-intro';
    this.config.steps.current = 'step-intro';
    this.setStep('step-intro');

    // Check how many options we have
    // If a customer only has name or logo embroidering available, dive directly into the existing one
    if (this.elements.controls.stepButtons.length === 1) {
      const step = this.elements.controls.stepButtons[0].dataset.embroideryStep;
      if (step === 'step-name' || step === 'step-logo') {
        this.state.embroidery.step = step;
        this.setStep(step);
      }
    }
  }

  /**
   * Initializes the configuration object with default values
   * Sets up body class, options, font options, and step configurations
   */
  initializeConfig() {
    // Get translations from data-options
    let translations = {
      product: 'Product',
      line: 'Line',
      font: 'Font',
      color: 'Color',
      text: 'Text',
    };
    try {
      const optionsData = this.dataset.options;
      if (optionsData) {
        translations = JSON.parse(optionsData);
      }
    } catch (error) {
      console.warn('Error parsing translations:', error);
    }

    this.config = {
      bodyClass: 'embroidery-drawer-is-open',
      options: translations,
      fontOptions: [
        'font-arial',
        'font-arial-black',
        'font-helvetica',
        'font-deja-vu-sans',
        'font-commercial-script',
        'font-adobe-caslon-pro-bold',
        'font-hobo',
        'font-lucida-calligraphy',
        'font-typewriter',
        'font-snickerdoodle',
        'font-milkshake',
        'font-doughnut',
        'font-cobbler',
        'font-gelato',
        'font-script',
        'font-times-new-roman',
        'font-century',
        'font-allegra-script',
        'font-argentia',
        'font-block',
        'font-brantford',
        'font-brush-script',
        'font-cin-italic',
        'font-copperplate',
        'font-designer',
        'font-diana-script',
        'font-evening-news',
        'font-goudy-bold',
        'font-lorraine',
        'font-nordique',
        'font-russ-times',
        'font-snow',
      ],
      steps: {
        options: [
          'step-intro',
          'step-name',
          'step-name-1-font',
          'step-name-1-color',
          'step-name-1-text',
          'step-name-2-font',
          'step-name-2-color',
          'step-name-2-text',
          'step-logo',
          'step-both',
          'step-summary',
        ],
        current: 'step-intro',
        next: '',
        previous: '',
      },
    };
  }

  /**
   * Initializes and caches DOM element references
   * Sets up references to containers, controls, preview elements, and summary elements
   */
  initializeElements() {
    this.elements = {
      container: this,
      body: document.querySelector('body'),
      form: this.closest('form'),
      cartNotification: document.querySelector('cart-notification'),
      cartDrawer: document.querySelector('cart-drawer'),
      controls: {
        closeButton: this.querySelector('[data-embroidery-close]'),
        stepButtons: this.querySelectorAll('[data-embroidery-step]'),
        sizeButtons: this.querySelectorAll('[data-embroidery-size]'),
        nextButton: this.querySelector('[data-embroidery-button-next]'),
        previousButton: this.querySelector('[data-embroidery-button-previous]'),
        nameLines: this.querySelectorAll('[name="embroideryNameLines"]'),
        agreeConditions: this.querySelector('[data-embroidery-agree-conditions]'),
      },
      preview: {
        container: this.querySelector('[data-embroidery-preview]'),
        line1: this.querySelectorAll('[data-embroidery-preview-line-1]'),
        line2: this.querySelectorAll('[data-embroidery-preview-line-2]'),
        button: this.querySelector('[data-embroidery-preview-button]'),
      },
      summary: {
        name: this.querySelector('[data-embroidery-summary-name]'),
        logos: this.querySelector('[data-embroidery-summary-logos]'),
        nameLines: {
          line1: {
            container: this.querySelector('[data-embroidery-summary-line-1-container]'),
            text: this.querySelector('[data-embroidery-summary-line-1-text]'),
            font: this.querySelector('[data-embroidery-summary-line-1-font]'),
            color: this.querySelector('[data-embroidery-summary-line-1-color]'),
            price: this.querySelector('[data-embroidery-summary-price-line-1]'),
          },
          line2: {
            container: this.querySelector('[data-embroidery-summary-line-2-container]'),
            text: this.querySelector('[data-embroidery-summary-line-2-text]'),
            font: this.querySelector('[data-embroidery-summary-line-2-font]'),
            color: this.querySelector('[data-embroidery-summary-line-2-color]'),
            price: this.querySelector('[data-embroidery-summary-price-line-2]'),
          },
        },
        addToCart: this.querySelector('[data-embroidery-add-to-cart]'),
        logoItems: this.querySelectorAll('[data-embroidery-summary-logo]'),
        amount: this.querySelector('[data-embroidery-summary-amount]'),
      },
    };
  }

  /**
   * Initializes the state object with default values
   * Sets up product information, embroidery options, and logo configurations
   */
  initializeState() {
    this.state = {
      product: {
        id: this.elements.form.querySelector('[name=id]').value,
        size: '',
        line1: this.dataset.productLine1,
        line2: this.dataset.productLine2,
      },
      bulkOrder: this.elements.form.dataset.bulkOrder === 'true',
      percentageComplete: 0,
      uniqueSKU: '',
      embroidery: {
        productName: this.querySelector('[data-embroidery-product-name]').innerHTML,
        step: '',
        name: {
          lines: '0',
          line1: {
            font: '',
            fontClass: 'arial',
            color: '',
            colorHex: '',
            text: 'Line 1',
          },
          line2: {
            font: '',
            fontClass: 'arial',
            color: '',
            colorHex: '',
            text: 'Line 2',
          },
        },
        logos: {
          mandatory: [],
          optional: [],
        },
        progressSteps: '',
        agreeConditions: false,
      },
    };
  }

  /**
   * Initializes the variant titles for all logos in the summary section
   * Creates and populates variant title elements for each logo
   */
  initializeLogoVariantTitles() {
    // Initialize variant titles for all logos in summary
    this.elements.summary.logoItems.forEach((summaryLogo) => {
      const container = this.querySelector(
        `.option[data-logo-id="${summaryLogo.dataset.embroiderySummaryLogo}"]`
      );
      if (!container) return;

      const select = container.querySelector('.logo-variants');
      if (!select) return;

      const selectedOption = select.options[select.selectedIndex];
      if (!selectedOption) return;

      const summaryTitle = summaryLogo.querySelector('.summary-title');
      if (summaryTitle) {
        const variantTitle = selectedOption.innerText.trim();
        let subTitleElement = summaryTitle.querySelector('.chosen-variant');
        if (!subTitleElement) {
          subTitleElement = document.createElement('div');
          subTitleElement.classList.add('chosen-variant', 'text-xs');
          summaryTitle.append(subTitleElement);
        }
        subTitleElement.textContent = variantTitle;
      }
    });
  }

  /**
   * Binds all necessary event listeners
   * Sets up handlers for buttons, inputs, and form controls
   */
  bindEvents() {
    // Close button event
    this.elements.controls.closeButton?.addEventListener('click', () => this.close());

    // Step buttons events
    this.elements.controls.stepButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const step = button.dataset.embroideryStep;
        this.state.embroidery.step = step;

        if (step === 'step-both') {
          this.config.steps.current = 'step-name';
        } else {
          this.config.steps.current = step;
        }

        this.setStep(this.config.steps.current);
      });
    });

    // Preview button event
    this.elements.preview.button?.addEventListener('click', () => {
      this.elements.preview.container.classList.toggle('full-preview');
    });

    // Navigation buttons events
    this.elements.controls.previousButton?.addEventListener('click', (e) => {
      if (this.config.steps.previous) {
        this.setStep(this.config.steps.previous);
      }
      e.currentTarget.blur();
    });

    this.elements.controls.nextButton?.addEventListener('click', (e) => {
      this.getCheckedValues();
      if (this.config.steps.next) {
        this.setStep(this.config.steps.next);
      }
      e.currentTarget.blur();
    });

    // Name lines events
    this.elements.controls.nameLines.forEach((input) => {
      input.addEventListener('change', (e) => {
        this.state.embroidery.name.lines = e.target.value;
        this.setStep('step-name');
        this.updateProgress();
      });
    });

    // Add to cart event
    this.elements.summary.addToCart?.addEventListener('click', (event) => {
      if (!this.state.embroidery.agreeConditions) {
        this.elements.controls.agreeConditions.classList.add('text-danger');
        this.elements.controls.agreeConditions.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      } else {
        this.addToCart(event);
      }
    });

    // Agree conditions event
    this.elements.controls.agreeConditions?.addEventListener('change', (e) => {
      this.state.embroidery.agreeConditions = e.target.checked;
    });

    // Get current size
    this.getCurrentSize();

    // Logo variant changes
    this.elements.container.querySelectorAll('.logo-variants').forEach((select) => {
      select.addEventListener('change', (e) => this.changeLogoVariant(e));
    });

    // Line 1 font input events
    this.querySelectorAll('[name="embroideryNameLine1Font"]').forEach((input) => {
      input.addEventListener('change', (e) => {
        this.state.embroidery.name.line1.font = e.target.value;

        if (e.target.dataset.embroidery_name_line_1_font_class) {
          this.state.embroidery.name.line1.fontClass =
            e.target.dataset.embroidery_name_line_1_font_class;
        }

        this.setStep('step-name-1-font');
        this.updateProgress();
      });
    });

    // Line 1 color input events
    this.querySelectorAll('[name="embroideryNameLine1Color"]').forEach((input) => {
      input.addEventListener('change', (e) => {
        this.state.embroidery.name.line1.color = e.target.value;

        if (e.target.dataset.embroideryNameLine1ColorHex) {
          this.state.embroidery.name.line1.colorHex = e.target.dataset.embroideryNameLine1ColorHex;
        } else if (e.target.dataset.embroidery_name_line_1_color_hex) {
          this.state.embroidery.name.line1.colorHex =
            e.target.dataset.embroidery_name_line_1_color_hex;
        }

        this.setStep('step-name-1-color');
        this.updateProgress();
      });
    });

    // Line 1 text input events
    this.querySelectorAll('[name="embroideryNameLine1Text"]').forEach((input) => {
      input.addEventListener('input', (e) => {
        this.state.embroidery.name.line1.text = e.target.value;
        this.setStep('step-name-1-text');
        this.updateProgress();
      });
    });

    // Line 2 font input events
    this.querySelectorAll('[name="embroideryNameLine2Font"]').forEach((input) => {
      input.addEventListener('change', (e) => {
        this.state.embroidery.name.line2.font = e.target.value;

        if (e.target.dataset.embroidery_name_line_2_font_class) {
          this.state.embroidery.name.line2.fontClass =
            e.target.dataset.embroidery_name_line_2_font_class;
        } else if (e.target.dataset.embroideryNameLine2FontClass) {
          this.state.embroidery.name.line2.fontClass =
            e.target.dataset.embroideryNameLine2FontClass;
        }

        this.setStep('step-name-2-font');
        this.updateProgress();
      });
    });

    // Line 2 color input events
    this.querySelectorAll('[name="embroideryNameLine2Color"]').forEach((input) => {
      input.addEventListener('change', (e) => {
        this.state.embroidery.name.line2.color = e.target.value;

        if (e.target.dataset.embroideryNameLine2ColorHex) {
          this.state.embroidery.name.line2.colorHex = e.target.dataset.embroideryNameLine2ColorHex;
        } else if (e.target.dataset.embroidery_name_line_2_color_hex) {
          this.state.embroidery.name.line2.colorHex =
            e.target.dataset.embroidery_name_line_2_color_hex;
        }

        this.setStep('step-name-2-color');
        this.updateProgress();
      });
    });

    // Line 2 text input events
    this.querySelectorAll('[name="embroideryNameLine2Text"]').forEach((input) => {
      input.addEventListener('input', (e) => {
        this.state.embroidery.name.line2.text = e.target.value;
        this.setStep('step-name-2-text');
        this.updateProgress();
      });
    });
  }

  /**
   * Gets and updates the current product size
   * Sets up size selector event listener and updates size buttons
   */
  getCurrentSize() {
    const sizeSelector = document.querySelector('.option__input-drawer:checked');
    if (sizeSelector) {
      this.updateSize(sizeSelector);
    }

    document.querySelector('[data-option-position="0"]')?.addEventListener('input', () => {
      const sizeSelector = document.querySelector('.option__input-drawer:checked');
      if (sizeSelector) {
        this.updateSize(sizeSelector);
      }
    });
  }

  /**
   * Updates the size information in the state and UI
   * @param {HTMLElement} sizeSelector - The selected size input element
   */
  updateSize(sizeSelector) {
    this.state.product.size = sizeSelector.value;
    this.elements.controls.sizeButtons.forEach((elem) => {
      elem.innerHTML = `${sizeSelector.name}: ${sizeSelector.value}`;
    });
  }

  /**
   * Gets checked values from logo options and updates state
   * Handles logo selections, font classes, and color hex values
   */
  getCheckedValues() {
    const logoGroup = this.querySelector('[data-embroidery-logo-container]');
    if (!logoGroup) return;

    const logoOptions = logoGroup.querySelectorAll('input:checked');
    logoOptions.forEach(({ value, name, type, dataset, checked }) => {
      if (name === 'embroideryAgreeConditions') {
        this.state.embroidery.agreeConditions = checked;
        return;
      }

      if (name in this.state.embroidery.name) {
        const value = type === 'checkbox' ? checked : value;
        this.state.embroidery.name[name] = value;

        // Update font classes and color hex values if available
        if (dataset.embroidery_name_line_1_font_class) {
          this.state.embroidery.name.line1.fontClass = dataset.embroidery_name_line_1_font_class;
        }
        if (dataset.embroidery_name_line_2_font_class) {
          this.state.embroidery.name.line2.fontClass = dataset.embroidery_name_line_2_font_class;
        }
        if (dataset.embroidery_name_line_1_color_hex) {
          this.state.embroidery.name.line1.colorHex = dataset.embroidery_name_line_1_color_hex;
        }
        if (dataset.embroidery_name_line_2_color_hex) {
          this.state.embroidery.name.line2.colorHex = dataset.embroidery_name_line_2_color_hex;
        }
      } else if (['embroideryLogosOptional[]', 'embroideryLogosMandatory[]'].includes(name)) {
        const targetName = name.replace('[]', '');
        const values = Array.from(this.querySelectorAll(`[name="${name}"]`))
          .filter((elem) => elem.checked)
          .map((elem) => elem.value);
        this.state.embroidery.logos[targetName.replace('embroideryLogos', '').toLowerCase()] =
          values;
      }
    });
  }

  /**
   * Updates the progress bar, preview, and summary sections
   * Called whenever the configuration state changes
   */
  updateProgress() {
    this.buildUniqueSKU();

    // Update progress bar and steps
    const progressBar = this.querySelector('[data-embroidery-percentage-bar]');
    const progressSteps = this.querySelector('[data-embroidery-progress-steps]');

    if (progressBar) {
      progressBar.style.width = this.state.percentageComplete + '%';
    }
    if (progressSteps) {
      progressSteps.innerHTML = this.state.embroidery.progressSteps;
    }

    // Update navigation buttons
    this.elements.controls.previousButton.disabled = !this.config.steps.previous;
    this.elements.controls.nextButton.disabled = !this.config.steps.next;

    // Update preview lines
    this.updatePreviewLines();

    // Update summary sections visibility
    this.updateSummaryVisibility();

    // Update summary text content
    this.updateSummaryContent();

    // Check and update logo selection
    this.checkLogoSelection();
  }

  /**
   * Updates the preview lines with current font and text settings
   * Handles both line 1 and line 2 preview elements
   */
  updatePreviewLines() {
    // Update line 1
    this.elements.preview.line1.forEach((elem) => {
      elem.classList.remove(...this.config.fontOptions);
      elem.classList.add(this.state.embroidery.name.line1.fontClass);
      elem.innerHTML = this.state.embroidery.name.line1.text;
      elem.style.color = this.state.embroidery.name.line1.colorHex;
    });

    // Update line 2
    this.elements.preview.line2.forEach((elem) => {
      const isHidden = this.state.embroidery.name.lines === '1';
      elem.classList.toggle('hidden', isHidden);

      if (!isHidden) {
        elem.classList.remove(...this.config.fontOptions);
        elem.classList.add(this.state.embroidery.name.line2.fontClass);
        elem.innerHTML = this.state.embroidery.name.line2.text;
        elem.style.color = this.state.embroidery.name.line2.colorHex;
      }
    });
  }

  /**
   * Updates the visibility of summary sections based on current step
   * Toggles visibility of name, logo, and price elements
   */
  updateSummaryVisibility() {
    const isLogoStep = this.state.embroidery.step === 'step-logo';
    const isNameStep = this.state.embroidery.step === 'step-name';

    this.elements.summary.name.classList.toggle('hidden', isLogoStep);
    this.elements.preview.container.classList.toggle('hidden', isLogoStep);
    this.elements.preview.button.classList.toggle('hidden', isLogoStep);
    this.elements.summary.logos.classList.toggle('hidden', isNameStep);

    // Update price visibility based on lines
    const hasOneLine = this.state.embroidery.name.lines === '1';
    this.elements.summary.nameLines.line1.price.classList.toggle('hidden', !hasOneLine);
    this.elements.summary.nameLines.line2.price.classList.toggle('hidden', hasOneLine);
    this.elements.summary.nameLines.line2.container.classList.toggle('hidden', hasOneLine);
  }

  /**
   * Updates the summary content with current configuration
   * Updates text, font, and color information for both lines
   */
  updateSummaryContent() {
    const { line1, line2 } = this.state.embroidery.name;

    // Update line 1 summary
    this.elements.summary.nameLines.line1.text.innerHTML = line1.text;
    this.elements.summary.nameLines.line1.font.innerHTML = line1.font;
    this.elements.summary.nameLines.line1.color.innerHTML = line1.color;

    // Update line 2 summary
    this.elements.summary.nameLines.line2.text.innerHTML = line2.text;
    this.elements.summary.nameLines.line2.font.innerHTML = line2.font;
    this.elements.summary.nameLines.line2.color.innerHTML = line2.color;
  }

  /**
   * Checks and updates logo selection visibility in summary
   * Shows/hides logos based on selection state
   */
  checkLogoSelection() {
    this.elements.summary.logoItems.forEach((elem) => {
      const logoId = elem.dataset.embroiderySummaryLogo;
      if (!logoId) return;

      const productLogoId = logoId.replace('optional_logo_', '').replace('mandatory_logo_', '');
      const isSelected =
        productLogoId &&
        (this.state.embroidery.logos.optional.includes(productLogoId) ||
          this.state.embroidery.logos.mandatory.includes(productLogoId));

      elem.classList.toggle('hidden', !isSelected);
    });
  }

  /**
   * Handles logo variant changes
   * Updates container, identifiers, and summary when variant is changed
   * @param {Event} event - The change event from the variant selector
   */
  changeLogoVariant(event) {
    const select = event.target;
    const selectedOption = select.options[select.selectedIndex];
    const container = select.closest('.option');
    if (!container) return;

    const {
      price: newPrice,
      image: newImageUrl,
      variantTitle: newVariantTitle,
    } = this.getLogoVariantData(selectedOption);

    const variant = container.getAttribute('data-variant');
    const oldLogoId = container.getAttribute('data-logo-id');
    const newLogoId = variant + select.value;

    this.updateLogoContainer(container, select.value, newPrice, newImageUrl);
    this.updateLogoIdentifiers(container, oldLogoId, newLogoId);
    this.updateSummaryLogo(oldLogoId, newLogoId, newImageUrl, newPrice, newVariantTitle);
  }

  /**
   * Gets logo variant data from selected option
   * @param {HTMLOptionElement} option - The selected option element
   * @returns {Object} Object containing price, image, and variant title
   */
  getLogoVariantData(option) {
    return {
      price: option.getAttribute('data-price'),
      image: option.getAttribute('data-image'),
      variantTitle: option.innerText.trim(),
    };
  }

  /**
   * Updates the logo container with new variant information
   * @param {HTMLElement} container - The logo container element
   * @param {string} newValue - The new variant value
   * @param {string} newPrice - The new variant price
   * @param {string} newImageUrl - The new variant image URL
   */
  updateLogoContainer(container, newValue, newPrice, newImageUrl) {
    container.querySelector('.option__input').value = newValue;

    const priceElement = container.querySelector('.price');
    if (priceElement && newPrice) {
      priceElement.innerHTML = newPrice;
    }

    const imageContainer = container.querySelector('.option__custom-checkbox');
    if (imageContainer && newImageUrl) {
      imageContainer.innerHTML = `<img src="${newImageUrl}" alt="Variant image"/>`;
    }
  }

  /**
   * Updates logo identifiers in container and summary
   * @param {HTMLElement} container - The logo container element
   * @param {string} oldLogoId - The previous logo ID
   * @param {string} newLogoId - The new logo ID
   */
  updateLogoIdentifiers(container, oldLogoId, newLogoId) {
    container.dataset.logoId = newLogoId;
    const innerSummaryLogo = container.querySelector('[data-embroidery-summary-logo]');
    if (innerSummaryLogo) {
      innerSummaryLogo.dataset.embroiderySummaryLogo = newLogoId;
    }
  }

  /**
   * Updates the summary logo information
   * @param {string} oldLogoId - The previous logo ID
   * @param {string} newLogoId - The new logo ID
   * @param {string} newImageUrl - The new image URL
   * @param {string} newPrice - The new price
   * @param {string} newVariantTitle - The new variant title
   */
  updateSummaryLogo(oldLogoId, newLogoId, newImageUrl, newPrice, newVariantTitle) {
    const summaryLogo = this.querySelector(`[data-embroidery-summary-logo="${oldLogoId}"]`);
    if (!summaryLogo) return;

    summaryLogo.dataset.embroiderySummaryLogo = newLogoId;

    const imageContainer = summaryLogo.querySelector('.img-container');
    if (imageContainer && newImageUrl) {
      imageContainer.innerHTML = `<img src="${newImageUrl}" alt="Variant image" class="flex-none" width="40" height="40" />`;
    }

    const priceElement = summaryLogo.querySelector('.text-xs.font-bold');
    if (priceElement && newPrice) {
      priceElement.textContent = newPrice;
    }

    const summaryTitle = summaryLogo.querySelector('.summary-title');
    if (summaryTitle) {
      let subTitleElement = summaryTitle.querySelector('.chosen-variant');
      if (subTitleElement) {
        subTitleElement.textContent = newVariantTitle;
      } else {
        subTitleElement = document.createElement('div');
        subTitleElement.classList.add('chosen-variant', 'text-xs');
        subTitleElement.textContent = newVariantTitle;
        summaryTitle.append(subTitleElement);
      }
    }
  }

  /**
   * Builds a unique SKU for the current configuration
   * @returns {string} The generated SKU
   */
  buildUniqueSKU() {
    let uniqueSKU = `${this.state.product.id}-${this.state.embroidery.step}`;

    if (this.state.embroidery.step !== 'step-logo') {
      uniqueSKU += this.buildNameSKUPart();
    }

    if (this.state.embroidery.step !== 'step-name') {
      uniqueSKU += this.buildLogoSKUPart();
    }

    this.state.uniqueSKU = this.convertToHandle(uniqueSKU);
    return this.state.uniqueSKU;
  }

  /**
   * Builds the name part of the SKU
   * @returns {string} The name portion of the SKU
   */
  buildNameSKUPart() {
    const { line1, line2, lines } = this.state.embroidery.name;
    let skuPart = `-line1-${line1.font}-${line1.color}-${line1.text}`;

    if (lines === '2') {
      skuPart += `-line2-${line2.font}-${line2.color}-${line2.text}`;
    }

    return skuPart;
  }

  /**
   * Builds the logo part of the SKU
   * @returns {string} The logo portion of the SKU
   */
  buildLogoSKUPart() {
    const { mandatory, optional } = this.state.embroidery.logos;
    return `-mandatory-logos-${mandatory.join('-')}-optional-logos-${optional.join('-')}`;
  }

  /**
   * Converts text to URL-friendly handle
   * @param {string} text - The text to convert
   * @returns {string} The converted handle
   */
  convertToHandle(text) {
    return text.replace(/\s+/g, '-').toLowerCase();
  }

  /**
   * Adds items to cart
   * @param {Event} event - The add to cart event
   */
  async addToCart(event) {
    this.elements.summary.addToCart.setAttribute('disabled', true);
    this.elements.summary.addToCart.classList.add('button--loading');

    try {
      const cartItems = await this.buildCartItems();
      const response = await this.sendCartRequest(cartItems);
      await this.handleCartResponse(response);
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      this.elements.summary.addToCart.classList.remove('button--loading');
      this.elements.summary.addToCart.removeAttribute('disabled');
    }
  }

  /**
   * Builds cart items from current configuration
   * @returns {Array} Array of cart items
   */
  buildCartItems() {
    const productItems = this.getProductItems();
    const allItems = [];

    productItems.forEach((productItem) => {
      this.state.product.id = productItem.id;
      const uniqueSKU = this.buildUniqueSKU();
      const variantNote = this.getVariantNote(productItem.id);
      const preOrderProperty = this.getPreOrderProperty();

      allItems.push(this.createMainItem(productItem, uniqueSKU, variantNote, preOrderProperty));

      if (this.state.embroidery.step !== 'step-logo') {
        this.addEmbroideryLines(allItems, productItem, uniqueSKU);
      }

      if (this.state.embroidery.step !== 'step-name') {
        this.addLogoItems(allItems, productItem, uniqueSKU);
      }
    });

    return allItems;
  }

  /**
   * Gets product items for cart
   * @returns {Array} Array of product items
   */
  getProductItems() {
    if (this.state.bulkOrder) {
      return Array.from(this.elements.form.querySelectorAll('[data-bulk-variant-id]'))
        .filter((elem) => elem.value > 0)
        .map((elem) => ({
          id: elem.dataset.bulkVariantId,
          quantity: elem.value,
          option: elem.dataset.bulkValue,
        }));
    }

    return [
      {
        id: this.elements.form.querySelector('[name=id]').value,
        quantity: 1,
        option: this.state.product.size,
      },
    ];
  }

  /**
   * Gets variant note for product
   * @param {string} variantId - The variant ID
   * @returns {Object} Variant note properties
   */
  getVariantNote(variantId) {
    const variantNote = this.elements.form.querySelector(`[data-variant-id-note="${variantId}"]`);
    if (!variantNote) return {};

    const input = variantNote.querySelector('[data-variant-input-note]');
    if (!input?.value) return {};

    const propertyName = input.getAttribute('name').replace('properties[', '').replace(']', '');
    return { [propertyName]: input.value };
  }

  /**
   * Gets pre-order property
   * @returns {Object} Pre-order property
   */
  getPreOrderProperty() {
    const input = this.elements.form.querySelector('[data-pre-order-input]');
    if (!input) return {};

    const propertyName = input.getAttribute('name').replace('properties[', '').replace(']', '');
    return { [propertyName]: input.value };
  }

  /**
   * Creates main cart item
   * @param {Object} productItem - The product item
   * @param {string} uniqueSKU - The unique SKU
   * @param {Object} variantNote - The variant note
   * @param {Object} preOrderProperty - The pre-order property
   * @returns {Object} The main cart item
   */
  createMainItem(productItem, uniqueSKU, variantNote, preOrderProperty) {
    return {
      id: productItem.id,
      quantity: productItem.quantity,
      properties: {
        _masterSKU: uniqueSKU,
        ...variantNote,
        ...preOrderProperty,
        ...this.getPartnerProperties(),
      },
    };
  }

  /**
   * Gets partner properties if applicable
   * @returns {Object} Partner properties
   */
  getPartnerProperties() {
    const partnerInputs = Array.from(
      this.elements.form.querySelectorAll('[data-partner-input-info]')
    );

    // Check if both partner name and restaurant inputs are filled
    const partnerNameInput = partnerInputs.find(
      (input) => input.dataset.partnerInputInfo === 'partnerName' && input.checked
    );
    const restaurantInput = partnerInputs.find(
      (input) => input.dataset.partnerInputInfo === 'partnerRestaurant' && input.value
    );

    if (partnerNameInput && restaurantInput) {
      // Get discount amount and type from partner input
      const discountAmount = partnerNameInput.getAttribute('data-discount-amount');
      const discountType = partnerNameInput.getAttribute('data-discount-type');

      const properties = {};

      // Add discount amount and type properties
      if (discountAmount) properties._discount_amount = discountAmount;
      if (discountType) properties._discount_type = discountType;

      // Add partner input properties
      partnerInputs.forEach((input) => {
        if ((input.type === 'radio' && input.checked) || (input.type !== 'radio' && input.value)) {
          const propertyName = input.name.replace('properties[', '').replace(']', '');
          properties[propertyName] = input.value;
        }
      });

      return properties;
    }

    return {};
  }

  /**
   * Adds embroidery lines to cart items
   * @param {Array} allItems - The cart items array
   * @param {Object} productItem - The product item
   * @param {string} uniqueSKU - The unique SKU
   */
  addEmbroideryLines(allItems, productItem, uniqueSKU) {
    const baseProperties = {
      [this.config.options.for]: `${this.state.embroidery.productName} (${productItem.option})`,
      _productID: productItem.id,
      _linkedSKU: uniqueSKU,
    };

    // Add line 1
    const line1Properties = {
      ...baseProperties,
      [this.config.options.text]: this.state.embroidery.name.line1.text,
      [this.config.options.font]: this.state.embroidery.name.line1.font,
      [this.config.options.color]: this.state.embroidery.name.line1.color,
      [this.config.options.line]: '1',
    };

    allItems.unshift({
      id: this.state.product.line1,
      quantity: productItem.quantity,
      properties: line1Properties,
    });

    // Add line 2 if needed
    if (this.state.embroidery.name.lines === '2') {
      const line2Properties = {
        ...baseProperties,
        [this.config.options.text]: this.state.embroidery.name.line2.text,
        [this.config.options.font]: this.state.embroidery.name.line2.font,
        [this.config.options.color]: this.state.embroidery.name.line2.color,
        [this.config.options.line]: '2',
      };

      allItems.unshift({
        id: this.state.product.line2,
        quantity: productItem.quantity,
        properties: line2Properties,
      });
    }
  }

  /**
   * Adds logo items to cart items
   * @param {Array} allItems - The cart items array
   * @param {Object} productItem - The product item
   * @param {string} uniqueSKU - The unique SKU
   */
  addLogoItems(allItems, productItem, uniqueSKU) {
    const baseProperties = {
      [this.config.options.for]: `${this.state.embroidery.productName} (${productItem.option})`,
      _productID: productItem.id,
      _linkedSKU: uniqueSKU,
      _mandatory: 'no', // default to optional
    };

    // Add mandatory logos
    this.state.embroidery.logos.mandatory.forEach((logo) => {
      allItems.unshift({
        id: logo,
        quantity: productItem.quantity,
        properties: {
          ...baseProperties,
          _mandatory: 'yes',
        },
      });
    });

    // Add optional logos
    this.state.embroidery.logos.optional.forEach((logo) => {
      allItems.unshift({
        id: logo,
        quantity: productItem.quantity,
        properties: baseProperties,
      });
    });
  }

  /**
   * Sends cart request to Shopify
   * @param {Array} items - The cart items
   * @returns {Promise<Object>} The cart response
   */
  async sendCartRequest(items) {
    const sectionsToFetch = [
      ...this.elements.cartDrawer.getSectionsToRender().map((section) => section.id),
      ...this.elements.cartNotification.getSectionsToRender().map((section) => section.id),
    ];

    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items,
        sections: sectionsToFetch,
        sections_url: window.location.pathname,
      }),
    });

    return response.json();
  }

  /**
   * Handles cart response
   * @param {Object} parsedState - The parsed response state
   */
  async handleCartResponse(parsedState) {
    if (parsedState.status === 422) {
      this.elements.cartNotification.setActiveElement(document.activeElement);
      this.elements.cartNotification.renderQuantityError(
        this.elements.form.querySelector('[name=inventory_quantity]').value
      );
      return;
    }

    await this.elements.cartDrawer.renderContents(parsedState);

    // Reset variant note fields
    this.elements.form
      .querySelectorAll('[data-variant-input-note]')
      .forEach((input) => (input.value = ''));

    // Close configurator and open cart drawer
    setTimeout(() => {
      this.close(true);
      this.elements.cartDrawer.open();
    });
  }

  /**
   * Closes the configurator
   * @param {boolean} closeAll - Whether to close all drawers
   */
  close(closeAll = false) {
    this.setStep('step-intro');

    // Hide the configurator element
    this.elements.container.classList.add('hidden');
    this.elements.body.classList.remove(this.config.bodyClass);

    if (closeAll || this.closest('[data-purchase-drawer-from-summary]'))
      this.elements.body.classList.remove('purchase-drawer-is-open');
  }

  /**
   * Sets the current step and updates UI
   * @param {string} step - The step to set
   */
  setStep(step = this.config.steps.current) {
    this.config.steps.current = step;

    // Remove all step classes and add the current one
    this.elements.container.classList.remove(...this.config.steps.options);
    this.elements.container.classList.add(step);

    // Ensure the container is visible when a step is set
    this.elements.container.classList.remove('hidden');

    const oneWeekSpan = document.querySelector('.embroidery-agree-conditions');
    const twoWeekSpan = document.querySelector('.embroidery-agree-conditions-logo');

    let totalSteps = 2;
    let currentStepNumber = 0;

    // Calculate total steps based on embroidery type
    if (this.state.embroidery.step === 'step-name') {
      totalSteps = this.state.embroidery.name.lines === '1' ? 4 : 7;
    } else if (this.state.embroidery.step === 'step-both') {
      totalSteps = this.state.embroidery.name.lines === '1' ? 5 : 8;
    }

    // Handle step transitions
    switch (step) {
      case 'step-intro':
        this.config.steps.previous = '';
        this.config.steps.next = 'step-name';
        break;

      case 'step-name':
        this.config.steps.previous = 'step-intro';
        this.config.steps.next = this.state.embroidery.name.lines === '0' ? '' : 'step-name-1-font';
        break;

      case 'step-name-1-font':
        this.config.steps.previous = 'step-name';
        this.config.steps.next = this.state.embroidery.name.line1.font ? 'step-name-1-color' : '';
        currentStepNumber = 1;
        break;

      case 'step-name-1-color':
        this.config.steps.previous = 'step-name-1-font';
        this.config.steps.next = this.state.embroidery.name.line1.color ? 'step-name-1-text' : '';
        currentStepNumber = 2;
        break;

      case 'step-name-1-text':
        this.config.steps.previous = 'step-name-1-color';
        currentStepNumber = 3;

        if (!this.state.embroidery.name.line1.text) {
          this.config.steps.next = '';
        } else if (this.state.embroidery.name.lines === '2') {
          this.config.steps.next = 'step-name-2-font';
        } else {
          this.config.steps.next =
            this.state.embroidery.step === 'step-both' ? 'step-logo' : 'step-summary';
        }
        break;

      case 'step-name-2-font':
        this.config.steps.previous = 'step-name-1-text';
        this.config.steps.next = this.state.embroidery.name.line2.font ? 'step-name-2-color' : '';
        currentStepNumber = 4;
        break;

      case 'step-name-2-color':
        this.config.steps.previous = 'step-name-2-font';
        this.config.steps.next = this.state.embroidery.name.line2.color ? 'step-name-2-text' : '';
        currentStepNumber = 5;
        break;

      case 'step-name-2-text':
        this.config.steps.previous = 'step-name-2-color';
        this.config.steps.next = this.state.embroidery.name.line2.text
          ? this.state.embroidery.step === 'step-both'
            ? 'step-logo'
            : 'step-summary'
          : '';
        currentStepNumber = 6;
        break;

      case 'step-logo': {
        this.config.steps.previous =
          this.state.embroidery.step === 'step-both'
            ? this.state.embroidery.name.lines === '1'
              ? 'step-name-1-text'
              : 'step-name-2-text'
            : 'step-intro';

        const logoContainer = this.querySelector('[data-embroidery-logo-container]');
        const hasLogos = logoContainer?.querySelectorAll('.option').length > 0;
        this.config.steps.next = hasLogos ? 'step-summary' : '';

        currentStepNumber =
          this.state.embroidery.step === 'step-both'
            ? this.state.embroidery.name.lines === '1'
              ? 5
              : 8
            : 1;
        break;
      }

      case 'step-summary':
        this.getCheckedValues();
        this.elements.controls.agreeConditions?.classList.remove('text-danger');

        if (this.state.embroidery.step === 'step-name') {
          oneWeekSpan?.classList.remove('hidden');
          twoWeekSpan?.classList.add('hidden');

          if (this.state.embroidery.name.lines === '1') {
            currentStepNumber = 4;
            this.config.steps.previous = 'step-name-1-text';
          } else {
            currentStepNumber = 7;
            this.config.steps.previous = 'step-name-2-text';
          }
        } else if (this.state.embroidery.step === 'step-both') {
          oneWeekSpan?.classList.add('hidden');
          twoWeekSpan?.classList.remove('hidden');
          this.config.steps.previous = 'step-logo';
          currentStepNumber = this.state.embroidery.name.lines === '1' ? 5 : 8;
        } else if (this.state.embroidery.step === 'step-logo') {
          oneWeekSpan?.classList.add('hidden');
          twoWeekSpan?.classList.remove('hidden');
          this.config.steps.previous = 'step-logo';
          currentStepNumber = 2;
        }

        this.config.steps.next = '';
        break;
    }

    // Update progress
    this.state.percentageComplete = (currentStepNumber / totalSteps) * 100;
    this.state.embroidery.progressSteps =
      currentStepNumber === 0 ? '' : `${currentStepNumber}/${totalSteps}`;
    this.updateProgress();
  }
}

// Initialize the custom element
if (!customElements.get('embroidery-configurator')) {
  customElements.define('embroidery-configurator', EmbroideryConfigurator);
}
